import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { TokenStorageService } from '../../../core/services/token-storage.service';

type AssistantState = 'idle' | 'thinking' | 'preview' | 'applying' | 'done' | 'error';

interface AiAction {
  action: string;
  confidence: number;
  requires_approval: boolean;
  data: Record<string, unknown>;
}

interface AiCommandResult {
  id: number;
  adminUserId: number;
  rawCommand: string;
  actionType: string;
  actionData: Record<string, unknown>;
  aiConfidence: number;
  status: string;
  rejectionReason?: string;
  errorMessage?: string;
  approvedBy?: number;
  approvedAt?: string;
  createdAt: string;
  appliedAt?: string;
  error?: string;
}

interface AiAdminActionLog {
  id: number;
  commandId: number;
  adminUserId: number;
  actionType: string;
  eventType: string;
  eventDetail: string;
  ipAddress: string;
  createdAt: string;
}

interface AiCommandDetail extends AiCommandResult {
  auditLogs: AiAdminActionLog[];
}

interface HistoryPage {
  content: AiCommandResult[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
}

@Component({
  selector: 'app-ai-admin-assistant',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './ai-admin-assistant.component.html',
  styleUrls: ['./ai-admin-assistant.component.scss'],
})
export class AiAdminAssistantComponent implements OnInit, OnDestroy {
  state: AssistantState = 'idle';
  activeTab: 'assistant' | 'history' = 'assistant';

  command = '';
  get userId(): number { return this.tokenStorage.getUserId() ?? 0; }

  modelLoaded = false;
  modelLoading = false;
  modelError: string | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  pending: AiCommandResult | null = null;
  rejectReason = '';
  errorMessage = '';

  history: AiCommandResult[] = [];
  historyPage = 0;
  historyTotal = 0;
  historyLoading = false;

  selectedCmd: AiCommandDetail | null = null;
  detailLoading = false;

  readonly ACTION_LABELS: Record<string, string> = {
    ADD_COMPLAINT_CATEGORY: 'Add Complaint Category',
    MODIFY_SLA: 'Modify SLA',
    CHANGE_ESCALATION_RULE: 'Change Escalation Rule',
    CREATE_NOTIFICATION_TEMPLATE: 'Create Notification Template',
    CREATE_REPORT: 'Create Report',
    ADD_DASHBOARD_WIDGET: 'Add Dashboard Widget',
    UNKNOWN: 'Unknown / Unsupported',
  };

  readonly ACTION_ICONS: Record<string, string> = {
    ADD_COMPLAINT_CATEGORY: 'category',
    MODIFY_SLA: 'schedule',
    CHANGE_ESCALATION_RULE: 'warning',
    CREATE_NOTIFICATION_TEMPLATE: 'notifications',
    CREATE_REPORT: 'assessment',
    ADD_DASHBOARD_WIDGET: 'dashboard',
    UNKNOWN: 'help_outline',
  };

  showingReject = false;

  readonly exampleCommands = [
    'Add a new complaint category called "Road Infrastructure" under "Public Works" with 10 day SLA',
    'Change the SLA for "Land Records" complaints to 5 working days',
    'Create an escalation rule: notify department head when 100% of SLA is consumed',
    'Create an SMS notification template for complaint received event',
    'Add a bar chart dashboard widget showing complaints by category for this month',
  ];

  constructor(private readonly http: HttpClient, private readonly tokenStorage: TokenStorageService) {}

  ngOnInit(): void {
    this.checkHealth();
    this.loadHistory();
  }

  ngOnDestroy(): void {
    this.stopPoll();
  }

  async checkHealth(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<any>(`${environment.apiHost.replace(/\/$/, '')}/api/v1/rnd/gemma-voice/health`)
      );
      const h = response?.data ?? response;
      this.modelLoaded = !!h?.modelLoaded;
      this.modelLoading = !!h?.modelLoading;
      this.modelError = h?.modelError ?? null;
      if (this.modelLoaded) {
        this.stopPoll();
      } else if (this.modelLoading) {
        this.startPoll();
      }
    } catch {
      this.modelLoaded = false;
      this.modelLoading = false;
    }
  }

  async loadModel(): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post<any>(`${environment.apiHost.replace(/\/$/, '')}/api/v1/rnd/gemma-voice/download`, {})
      );
      this.modelLoading = true;
      this.modelError = null;
      this.startPoll();
    } catch (err: any) {
      this.modelError = err?.error?.message ?? 'Failed to trigger model load';
    }
  }

  private startPoll(): void {
    if (this.pollTimer) return;
    this.pollTimer = setInterval(() => this.checkHealth(), 5000);
  }

  private stopPoll(): void {
    if (!this.pollTimer) return;
    clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  get actionLabel(): string {
    return this.ACTION_LABELS[this.pending?.actionType ?? ''] ?? this.pending?.actionType ?? '-';
  }

  get actionIcon(): string {
    return this.ACTION_ICONS[this.pending?.actionType ?? ''] ?? 'smart_toy';
  }

  get confidencePercent(): number {
    return Math.round((this.pending?.aiConfidence ?? 0) * 100);
  }

  get confidenceClass(): string {
    const pct = this.confidencePercent;
    if (pct >= 80) return 'high';
    if (pct >= 50) return 'medium';
    return 'low';
  }

  get dataEntries(): Array<{ key: string; value: unknown }> {
    const data = this.pending?.actionData ?? {};
    return Object.entries(data).map(([key, value]) => ({ key, value }));
  }

  useExample(example: string): void {
    this.command = example;
  }

  async submit(): Promise<void> {
    if (!this.command.trim()) return;
    this.state = 'thinking';
    this.pending = null;
    this.errorMessage = '';

    try {
      const response = await firstValueFrom(
        this.http.post<any>(`${this.apiBase}/command`, {
          command: this.command.trim(),
          userId: this.userId,
        })
      );
      const result: AiCommandResult = response?.data ?? response;

      if (result.error) {
        this.state = 'error';
        this.errorMessage = result.error;
        return;
      }
      this.pending = result;
      this.state = 'preview';
    } catch (err: any) {
      this.state = 'error';
      this.errorMessage = err?.error?.message ?? err?.message ?? 'Failed to process command';
    }
  }

  async approve(): Promise<void> {
    if (!this.pending) return;
    this.state = 'applying';
    try {
      const response = await firstValueFrom(
        this.http.post<any>(`${this.apiBase}/approve/${this.pending.id}`, {
          approverId: this.userId,
        })
      );
      this.pending = response?.data ?? response;
      this.state = 'done';
      this.loadHistory();
    } catch (err: any) {
      this.state = 'error';
      this.errorMessage = err?.error?.message ?? err?.message ?? 'Failed to apply action';
    }
  }

  async reject(): Promise<void> {
    if (!this.pending) return;
    try {
      await firstValueFrom(
        this.http.post<any>(`${this.apiBase}/reject/${this.pending.id}`, {
          rejectorId: this.userId,
          reason: this.rejectReason || 'Rejected by admin',
        })
      );
      this.reset();
      this.loadHistory();
    } catch (err: any) {
      this.errorMessage = err?.error?.message ?? 'Failed to reject command';
    }
  }

  showReject(): void {
    this.showingReject = true;
  }

  reset(): void {
    this.state = 'idle';
    this.pending = null;
    this.rejectReason = '';
    this.errorMessage = '';
    this.showingReject = false;
  }

  async loadHistory(page = 0): Promise<void> {
    this.historyLoading = true;
    this.historyPage = page;
    try {
      const response = await firstValueFrom(
        this.http.get<any>(`${this.apiBase}/history?page=${page}&size=15`)
      );
      const paged: HistoryPage = response?.data ?? response;
      this.history = paged.content ?? [];
      this.historyTotal = paged.totalElements ?? 0;
    } catch {
      this.history = [];
    } finally {
      this.historyLoading = false;
    }
  }

  statusClass(status: string): string {
    switch (status) {
      case 'APPLIED': return 'success';
      case 'PENDING': return 'warning';
      case 'APPROVED': return 'info';
      case 'REJECTED':
      case 'FAILED': return 'danger';
      default: return 'secondary';
    }
  }

  formatKey(key: string): string {
    return key.replaceAll('_', ' ').replace(/([A-Z])/g, ' $1').trim()
      .split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
  }

  async openDetail(cmd: AiCommandResult): Promise<void> {
    this.selectedCmd = { ...cmd, auditLogs: [] };
    this.detailLoading = true;
    try {
      const response = await firstValueFrom(
        this.http.get<any>(`${this.apiBase}/command/${cmd.id}`)
      );
      this.selectedCmd = response?.data ?? response;
    } catch {
      // keep the basic data already set
    } finally {
      this.detailLoading = false;
    }
  }

  closeDetail(): void {
    this.selectedCmd = null;
    this.detailLoading = false;
  }

  actionDataEntries(data: Record<string, unknown>): Array<{ key: string; value: unknown }> {
    return Object.entries(data)
      .filter(([key]) => !key.startsWith('__'))
      .map(([key, value]) => ({ key, value }));
  }

  confidenceClassFor(confidence: number): string {
    const pct = Math.round(confidence * 100);
    if (pct >= 80) return 'high';
    if (pct >= 50) return 'medium';
    return 'low';
  }

  auditDotClass(eventType: string): string {
    switch (eventType) {
      case 'APPLIED':   return 'dot-success';
      case 'APPROVED':  return 'dot-info';
      case 'SUBMITTED': return 'dot-neutral';
      case 'REJECTED':
      case 'FAILED':    return 'dot-danger';
      default:          return 'dot-neutral';
    }
  }

  private get apiBase(): string {
    return `${environment.apiHost.replace(/\/$/, '')}/api/v1/rnd/ai-admin`;
  }
}
